import React, { useState, type FC } from "react";
import { Alert, InputNumber, Spin, message } from "antd";
import { Modal } from "../../../../components/shared";
import { useSeriesBrazePayload } from "../../../../queries/dynamicBonuses/useSeriesBrazePayload";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import { getApiErrorMessage } from "../../../../utils/ErrorUtils";
import styles from "../stylesDynamicBonuses.module.css";

const DEFAULT_EXPIRY_HOURS = 168;
const SEND_TYPE = "scheduled" as const;

type BrazePayloadModalProps = {
  open: boolean;
  onClose: () => void;
  seriesId: string;
  /**
   * Override the clipboard write. Defaults to `navigator.clipboard.writeText`
   * — exposed for tests and for environments where the API isn't available.
   */
  onCopy?: (text: string) => void;
};

const BrazePayloadModal: FC<BrazePayloadModalProps> = ({
  open,
  onClose,
  seriesId,
  onCopy,
}) => {
  const [expiryHours, setExpiryHours] = useState(DEFAULT_EXPIRY_HOURS);

  const { data, isLoading, isError, error } = useSeriesBrazePayload(seriesId, {
    expiryHours,
    sendType: SEND_TYPE,
    enabled: open,
  });

  const payloadJson = data ? JSON.stringify(data.payload, null, 2) : "";
  const showNotActiveWarning = Boolean(
    data?.warnings?.includes("series_not_active"),
  );

  const handleCopy = () => {
    if (!payloadJson) return;
    if (onCopy) {
      onCopy(payloadJson);
      message.success(AppStrings.PAM_COPIED_TO_CLIPBOARD);
      return;
    }
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(payloadJson).then(() => {
        message.success(AppStrings.PAM_COPIED_TO_CLIPBOARD);
      });
    }
  };

  const handleClose = () => {
    setExpiryHours(DEFAULT_EXPIRY_HOURS);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={AppStrings.PAM_DYNAMIC_BONUS_BRAZE_PAYLOAD_TITLE}
      size="md"
      primaryAction={{
        label: AppStrings.PAM_DYNAMIC_BONUS_BRAZE_PAYLOAD_COPY,
        onClick: handleCopy,
        disabled: !payloadJson,
      }}
      secondaryAction={{
        label: AppStrings.CANCEL,
        onClick: handleClose,
      }}
      destroyOnClose
    >
      <p className={styles.brazePayloadHint}>
        {AppStrings.PAM_DYNAMIC_BONUS_BRAZE_PAYLOAD_DESCRIPTION}
      </p>

      <div style={{ marginBottom: 16 }}>
        <label>
          <div style={{ marginBottom: 4 }}>
            {AppStrings.PAM_DYNAMIC_BONUS_BRAZE_PAYLOAD_EXPIRY_LABEL}
          </div>
          <InputNumber
            min={1}
            max={8760}
            value={expiryHours}
            onChange={(next) => {
              if (typeof next === "number" && Number.isFinite(next)) {
                setExpiryHours(next);
              }
            }}
            aria-label={AppStrings.PAM_DYNAMIC_BONUS_BRAZE_PAYLOAD_EXPIRY_LABEL}
          />
        </label>
      </div>

      {showNotActiveWarning && (
        <Alert
          style={{ marginBottom: 12 }}
          type="warning"
          showIcon
          message={AppStrings.PAM_DYNAMIC_BONUS_BRAZE_PAYLOAD_NOT_ACTIVE_WARNING}
        />
      )}

      {isLoading && <Spin data-testid="braze-payload-loading" />}

      {isError && (
        <p role="alert">
          {getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED)}
        </p>
      )}

      {data && !isLoading && (
        <>
          <p className={styles.brazePayloadHint}>
            {formatString(AppStrings.PAM_DYNAMIC_BONUS_BRAZE_PAYLOAD_WEBHOOK_HINT, {
              webhookPath: data.webhook_path,
            })}
          </p>
          <pre
            data-testid="braze-payload-json"
            className={styles.brazePayloadJson}
          >
            {payloadJson}
          </pre>
        </>
      )}
    </Modal>
  );
};

export default BrazePayloadModal;
